import * as React from 'react'
import { Link, Navigate, useParams } from 'react-router-dom'
import { ChevronLeft, LayoutGrid, Loader2 } from 'lucide-react'
import {
  useVenue,
  useUpdateSpace,
  useAddSpacePhoto,
  useUpdateSpacePhoto,
  useDeleteSpacePhoto,
  useReorderSpacePhotos,
} from '@/hooks/useVenues'
import { useAuth } from '@/auth/AuthContext'
import { can } from '@/auth/access'
import { requestErrorMessage } from '@/helpers/apiClient'
import { SpacePhotosField } from '@/components/SpacePhotosField'
import { Field } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { PageHeader, SectionHeader } from '@/components/ui/page-header'
import { FormError } from '@/components/ui/form-error'
import { feetToPixels, pixelsToFeet } from '@lib/constants/scale'

type SpaceSizeEdit =
  | { kind: 'invalid' }
  | { kind: 'unchanged' }
  | { kind: 'change'; input: { width: number } | { height: number } }

/** Whole feet shown for a stored pixel length. */
export function feetOf(pixels: number): number {
  return Math.round(pixelsToFeet(pixels))
}

/**
 * One axis of a room, as typed. Only that axis is returned. Pairing it with
 * the other axis's rounded feet rewrote a size nobody edited, and blurring
 * both fields kept only the last one.
 */
export function spaceSizeEdit(axis: 'width' | 'height', typed: string, storedPx: number): SpaceSizeEdit {
  const n = Number(typed)
  if (!Number.isFinite(n) || n <= 0) return { kind: 'invalid' }
  if (n === feetOf(storedPx)) return { kind: 'unchanged' }
  const pixels = feetToPixels(n)
  return { kind: 'change', input: axis === 'width' ? { width: pixels } : { height: pixels } }
}

/**
 * A room's own settings: what it is called, how big it is, and what it looks like.
 *
 * This page exists because there was nowhere else for any of it. Everything about a
 * space lived in the floor-plan editor — even renaming one meant opening a canvas
 * with drawing tools on it — so a photograph would have meant opening that editor
 * to attach a photograph, which is the wrong door by a long way.
 *
 * The editor keeps the floor plan, reached from here and from the spaces list under
 * its own name. Drawing a room and describing a room are different jobs.
 */
export function VenueSpaceSettingsPage(): React.ReactElement {
  const { venueId, spaceId } = useParams<{ venueId: string; spaceId: string }>()
  const { user } = useAuth()
  const canEdit = can(user, 'spaces:edit')
  const { data: venue, isLoading } = useVenue(venueId ?? '')
  const updateSpace = useUpdateSpace()
  const addPhoto = useAddSpacePhoto()
  const captionPhoto = useUpdateSpacePhoto()
  const removePhoto = useDeleteSpacePhoto()
  const reorderPhotos = useReorderSpacePhotos()
  const [error, setError] = React.useState<string | null>(null)
  const [name, setName] = React.useState<string | null>(null)

  if (venueId === undefined || spaceId === undefined) return <Navigate to="/venues/all" replace />
  // Narrowed once, so every handler below closes over a string rather than
  // re-checking a value the route already guarantees.
  const vId: string = venueId
  const sId: string = spaceId

  const space = venue?.spaces.find((s) => s.id === sId)

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 p-6 text-sm text-ink-muted">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading…
      </div>
    )
  }
  if (!venue || !space) {
    return (
      <div className="space-y-2 p-6">
        <p className="text-sm text-ink-muted">This space could not be found.</p>
        <Link to={`/venues/${vId}/settings`} className="text-sm underline">
          Back to the venue
        </Link>
      </div>
    )
  }

  const busy =
    addPhoto.isPending || captionPhoto.isPending || removePhoto.isPending || reorderPhotos.isPending
  const shown = name ?? space.name

  function onRename(): void {
    const trimmed = shown.trim()
    if (trimmed === '' || trimmed === space?.name) {
      setName(null)
      return
    }
    setError(null)
    updateSpace.mutate(
      { venueId: vId, spaceId: sId, input: { name: trimmed } },
      { onSuccess: () => setName(null), onError: (e) => setError(requestErrorMessage(e)) },
    )
  }

  function commitSize(axis: 'width' | 'height', raw: string): void {
    if (!space) return
    const stored = axis === 'width' ? space.width : space.height
    const edit = spaceSizeEdit(axis, raw, stored)
    if (edit.kind === 'invalid') {
      setError('Enter a size greater than 0.')
      return
    }
    if (edit.kind === 'unchanged') return
    setError(null)
    updateSpace.mutate(
      { venueId: vId, spaceId: sId, input: edit.input },
      { onError: (e) => setError(requestErrorMessage(e)) },
    )
  }

  const fail = (e: Error): void => setError(requestErrorMessage(e))

  return (
    <div className="mx-auto max-w-4xl space-y-8 p-4 sm:p-6">
      <div>
        <Link
          to={`/venues/${vId}/settings`}
          className="mb-2 inline-flex items-center gap-1 text-sm text-ink-muted transition-colors hover:text-ink"
        >
          <ChevronLeft className="h-4 w-4" /> {venue.name}
        </Link>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <PageHeader
            title={space.name}
            description="What this room is called, how big it is, and what it looks like."
            meta={`${String(feetOf(space.width))} × ${String(feetOf(space.height))} ft`}
          />
          <Button asChild variant="outline" size="sm">
            <Link to={`/venues/${vId}/spaces/${sId}`}>
              <LayoutGrid className="h-4 w-4" /> Edit floor plan
            </Link>
          </Button>
        </div>
      </div>

      {error ? <FormError>{error}</FormError> : null}

      <section className="space-y-4">
        <SectionHeader
          title="Photographs"
          description="Shown to anyone looking at an event in this room, clients included. The first is the one used where only one will fit."
        />
        <SpacePhotosField
          photos={space.photos}
          canEdit={canEdit}
          busy={busy}
          onAdd={(image) => {
            setError(null)
            addPhoto.mutate({ venueId: vId, spaceId: sId, input: { image } }, { onError: fail })
          }}
          onCaption={(photoId, caption) => {
            setError(null)
            captionPhoto.mutate({ venueId: vId, spaceId: sId, photoId, input: { caption } }, { onError: fail })
          }}
          onFocalPoint={(photoId, focalPoint) => {
            setError(null)
            captionPhoto.mutate({ venueId: vId, spaceId: sId, photoId, input: { focalPoint } }, { onError: fail })
          }}
          onRemove={(photoId) => {
            setError(null)
            removePhoto.mutate({ venueId: vId, spaceId: sId, photoId }, { onError: fail })
          }}
          onReorder={(photoIds) => {
            setError(null)
            reorderPhotos.mutate({ venueId: vId, spaceId: sId, input: { photoIds } }, { onError: fail })
          }}
        />
      </section>

      {canEdit ? (
        <section className="space-y-4 border-t border-line pt-6">
          <SectionHeader
            title="Name and size"
            description="Renaming a room used to mean opening the floor-plan editor. It does not now."
          />
          <div className="flex flex-wrap items-end gap-3">
            <Field label="Name" className="min-w-[16rem] flex-1">
              <Input
                value={shown}
                maxLength={200}
                onChange={(e) => setName(e.target.value)}
                onBlur={onRename}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') onRename()
                  if (e.key === 'Escape') setName(null)
                }}
              />
            </Field>
            <Field label="Width (ft)" className="w-28">
              <Input
                type="number"
                min={1}
                defaultValue={feetOf(space.width)}
                onBlur={(e) => commitSize('width', e.target.value)}
              />
            </Field>
            <Field label="Length (ft)" className="w-28">
              <Input
                type="number"
                min={1}
                defaultValue={feetOf(space.height)}
                onBlur={(e) => commitSize('height', e.target.value)}
              />
            </Field>
            {updateSpace.isPending ? (
              <Loader2 className="mb-2 h-4 w-4 animate-spin text-ink-muted" />
            ) : null}
          </div>
        </section>
      ) : null}
    </div>
  )
}
